import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, addHours } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tractor, Location } from "@/types/tms";
import { Wrench, Clock, FileText } from "lucide-react";

interface WorkOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tractor: (Tractor & { home_base: Location | null }) | null;
  tractorCurrentLocation: string | null;
}

export function WorkOrderDialog({
  open,
  onOpenChange,
  tractor,
  tractorCurrentLocation,
}: WorkOrderDialogProps) {
  const queryClient = useQueryClient();
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    return format(now, "yyyy-MM-dd'T'HH:mm");
  });
  const [durationHours, setDurationHours] = useState("4");
  const [description, setDescription] = useState("");

  const createWorkOrder = useMutation({
    mutationFn: async () => {
      if (!tractor) throw new Error("No tractor selected");

      const startDate = new Date(startTime);
      const duration = parseFloat(durationHours) || 4;

      // Get or create sequence for this tractor
      const { data: existingSeq } = await supabase
        .from("tractor_work_order_sequences")
        .select("last_sequence_number")
        .eq("tractor_id", tractor.id)
        .single();

      let nextSeq: number;
      if (existingSeq) {
        // Update existing sequence
        const { data: updatedSeq, error: seqError } = await supabase
          .from("tractor_work_order_sequences")
          .update({ last_sequence_number: existingSeq.last_sequence_number + 1 })
          .eq("tractor_id", tractor.id)
          .select("last_sequence_number")
          .single();
        if (seqError) throw seqError;
        nextSeq = updatedSeq.last_sequence_number;
      } else {
        // Insert new sequence
        const { data: newSeq, error: insertError } = await supabase
          .from("tractor_work_order_sequences")
          .insert({ tractor_id: tractor.id, last_sequence_number: 1 })
          .select("last_sequence_number")
          .single();
        if (insertError) throw insertError;
        nextSeq = newSeq.last_sequence_number;
      }

      // Generate unique ID: WO-[TractorName]-[Sequence]
      const uniqueId = `WO-${tractor.name}-${String(nextSeq).padStart(3, "0")}`;

      // Create work order in new table
      const { data: workOrder, error: orderError } = await supabase
        .from("work_orders")
        .insert({
          unique_id: uniqueId,
          tractor_id: tractor.id,
          start_time: startDate.toISOString(),
          duration_hours: duration,
          description: description || null,
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Add work order ID to tractor's work_order_ids array
      const currentWorkOrderIds = tractor.work_order_ids || [];
      const { error: tractorError } = await supabase
        .from("tractors")
        .update({ work_order_ids: [...currentWorkOrderIds, workOrder.id] })
        .eq("id", tractor.id);

      if (tractorError) throw tractorError;

      // Calculate end time for overlap check
      const endDate = addHours(startDate, duration);

      // Unassign this tractor from any overlapping orders
      // Overlap means: order starts before work order ends AND order ends after work order starts
      const { data: overlappingOrders, error: fetchError } = await supabase
        .from("orders")
        .select("id, unique_id")
        .eq("assigned_tractor_id", tractor.id)
        .lt("ready_time", endDate.toISOString())
        .gt("requested_delivery_time_end", startDate.toISOString());

      if (fetchError) throw fetchError;

      if (overlappingOrders && overlappingOrders.length > 0) {
        const { error: unassignError } = await supabase
          .from("orders")
          .update({ assigned_tractor_id: null })
          .in("id", overlappingOrders.map((o) => o.id));

        if (unassignError) throw unassignError;

        // Log the unassignments
        for (const order of overlappingOrders) {
          await supabase.from("order_change_log").insert({
            order_id: order.id,
            field_name: "assigned_tractor_id",
            old_value: tractor.name,
            new_value: null,
            changed_by: "System (Work Order)",
          });
        }
      }

      return { workOrder, unassignedCount: overlappingOrders?.length || 0 };
    },
    onSuccess: ({ unassignedCount }) => {
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["tractors-with-home-base"] });
      
      let message = "Work order created successfully";
      if (unassignedCount > 0) {
        message += `. ${unassignedCount} overlapping order(s) had tractor unassigned.`;
      }
      toast.success(message);
      
      onOpenChange(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create work order: ${error.message}`);
    },
  });

  const resetForm = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    setStartTime(format(now, "yyyy-MM-dd'T'HH:mm"));
    setDurationHours("4");
    setDescription("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  if (!tractor) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-[hsl(var(--status-in-transit))]" />
            Create Work Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tractor Info */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="text-sm font-medium">{tractor.name}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Current location: {tractorCurrentLocation || tractor.home_base?.city || "Home base"}
            </div>
            <div className="text-xs text-muted-foreground">
              Will return to: {tractor.home_base?.short_code || "Unknown home base"}
            </div>
          </div>

          {/* Start Time */}
          <div className="space-y-2">
            <Label htmlFor="start-time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Start Time
            </Label>
            <Input
              id="start-time"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration">Duration (hours)</Label>
            <Input
              id="duration"
              type="number"
              min="0.5"
              step="0.5"
              value={durationHours}
              onChange={(e) => setDurationHours(e.target.value)}
              placeholder="4"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Description (optional)
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Oil change, brake inspection, tire rotation..."
              rows={3}
            />
          </div>

          {/* Warning */}
          <div className="text-xs text-muted-foreground bg-[hsl(var(--status-in-transit)/0.1)] p-3 rounded-lg">
            <strong>Note:</strong> Creating this work order will unassign this tractor from any overlapping orders.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createWorkOrder.mutate()}
            disabled={createWorkOrder.isPending}
            className="bg-[hsl(var(--status-in-transit))] hover:bg-[hsl(var(--status-in-transit)/0.9)]"
          >
            {createWorkOrder.isPending ? "Creating..." : "Create Work Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
